import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Stack, styled, Typography } from '@mui/material'
import type { BoxProps } from '@mui/material'
import { Fragment, useMemo, useRef, useState } from 'react'
import type { FC, ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import type { HoldingWeekDto } from '@hcr/api/consumer'
import { COLORS, ICONS, unit } from '@hcr/ui'

import { getYearHeadingId, YearHeading } from '../common/year-heading'
import { prepareWeekPreviewsData } from '../utils'

import { WeekPreview } from './week-preview'

const WEEKS_PREVIEW_LIMIT = 5

interface VillaWeeksPreviewProps {
  holdingId: string
  weeks: HoldingWeekDto[]
}

export const VillaWeeksPreview: FC<VillaWeeksPreviewProps> = ({ weeks, holdingId }) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const weekRefs = useRef(new Map<string, HTMLAnchorElement>())

  const weekPreviewsData = useMemo(() => prepareWeekPreviewsData(weeks ?? []), [weeks])

  const orderedWeekStarts = useMemo(
    () => Array.from(weekPreviewsData.values()).flatMap(yearWeeks => yearWeeks.map(week => week.week_start)),
    [weekPreviewsData]
  )

  const totalWeeksCount = orderedWeekStarts.length

  const limitedWeekPreviewsData = useMemo(() => {
    const { limited } = Array.from(weekPreviewsData.entries()).reduce(
      (acc, [year, yearWeeks]) => {
        if (acc.remaining <= 0) return acc

        const limitedYearWeeks = yearWeeks.slice(0, acc.remaining)
        acc.limited.set(year, limitedYearWeeks)

        return { limited: acc.limited, remaining: acc.remaining - limitedYearWeeks.length }
      },
      { limited: new Map<string, HoldingWeekDto[]>(), remaining: WEEKS_PREVIEW_LIMIT }
    )

    return limited
  }, [weekPreviewsData])

  const hasMoreWeeks = totalWeeksCount > WEEKS_PREVIEW_LIMIT
  const yearGroups = Array.from((isExpanded ? weekPreviewsData : limitedWeekPreviewsData).entries())

  const handleToggleExpanded = () => {
    const firstNewWeekStart = orderedWeekStarts[WEEKS_PREVIEW_LIMIT]

    setIsExpanded(prevIsExpanded => !prevIsExpanded)

    if (!isExpanded && firstNewWeekStart) {
      requestAnimationFrame(() => {
        weekRefs.current.get(firstNewWeekStart)?.focus()
      })
    }
  }

  return (
    <Stack
      bgcolor={COLORS.hiekka[500]}
      width='100%'
      paddingX={unit(1.75)}
      paddingBottom={unit(1.75)}
      marginBottom={unit(1.75)}
    >
      {yearGroups.map(([year, yearWeeks], groupIndex) => (
        <Fragment key={year}>
          <YearHeading
            year={year}
            sx={{
              paddingTop: unit(6),
              paddingBottom: unit(2),
              paddingLeft: unit(3.75),
              paddingRight: unit(4.25),
            }}
          />
          <Stack component='section' aria-labelledby={getYearHeadingId(year)} bgcolor={COLORS.vaalea['60%']}>
            <WeeksList component='ul'>
              {yearWeeks.map((week, index) => (
                <WeekItem key={week.week_start} component='li' $hasBorder={index > 0}>
                  <WeekPreview
                    holdingId={holdingId}
                    week={week}
                    actionRef={element => {
                      if (element) {
                        weekRefs.current.set(week.week_start, element)
                      } else {
                        weekRefs.current.delete(week.week_start)
                      }
                    }}
                  />
                </WeekItem>
              ))}
            </WeeksList>
            {hasMoreWeeks && groupIndex === yearGroups.length - 1 && (
              <ToggleButton onClick={handleToggleExpanded} disableElevation>
                <Typography variant='textLinkM'>
                  {t(isExpanded ? 'owner-holding.fewer-weeks' : 'owner-holding.more-weeks')}
                </Typography>
                <FontAwesomeIcon
                  icon={isExpanded ? ICONS.farArrowUp : ICONS.farArrowDown}
                  color={COLORS.burgundi[500]}
                  aria-hidden='true'
                />
              </ToggleButton>
            )}
          </Stack>
        </Fragment>
      ))}
    </Stack>
  )
}

const WeeksList = styled(Box)`
  list-style: none;
  margin: 0;
  padding: 0;
` as (props: BoxProps<'ul'>) => ReactElement

type WeekItemProps = BoxProps<'li', { $hasBorder: boolean }>

const WeekItem = styled(Box, { shouldForwardProp: prop => prop !== '$hasBorder' })<{
  $hasBorder: boolean
}>`
  border-top: ${props => (props.$hasBorder ? `${unit(0.25)} solid ${COLORS.hiekka[500]}` : 'none')};
` as (props: WeekItemProps) => ReactElement

const ToggleButton = styled(Button)`
  display: flex;
  width: 100%;
  gap: ${unit(1.5)};
  padding-top: ${unit(4.25)};
  padding-bottom: ${unit(4.75)};
  min-width: 0;
  border-radius: 0;
  text-transform: none;
  background-color: ${COLORS.hiekka[500]};

  &:hover {
    background-color: ${COLORS.hiekka[500]};
  }
`
